import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { Save, RotateLeft, AddCircle, Delete } from "@mui/icons-material";
import configServ from "../../services/config";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../redux/reducers/LoaderTrigger";
import Swal from "sweetalert2";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const PrescriptionDetailsCustomer = ({
  item_id,
  customer_id,
  diagnosis_id,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { company_id, response_id, user_id } = useSelector(
    (state) => state.GlobalVariables
  );
  const [itemList, setItemList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ prescribed_by: "" });
  const [validationErrors, setValidationErrors] = useState({});
  const [practitioners, setPractitioners] = useState([]);
  const [solutions, setSolutions] = useState([]);

  useEffect(() => {
    const fetchPractitioner = async () => {
      try {
        const res = await configServ.getPractitioners({ company_id });
        setPractitioners(res.status === 200 ? res.data : []);
      } catch (e) {
        console.log(e);
      }
    };
    fetchPractitioner();
  }, []);

  useEffect(() => {
    const fetchItemList = async () => {
      try {
        setLoading(true);
        const res = await configServ.getSolutionsBySubCategory({
          company_id,
          item_id,
        });
        setItemList(
          res.status === 200
            ? res.data.map((item) => ({
                label: item.item_name,
                value: item.id,
              }))
            : []
        );
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };
    if (item_id !== null) fetchItemList();
  }, [item_id]);

  const handleSolutionChange = (index, field, value) => {
    setSolutions((prev) => {
      const updatedSolutions = prev.map((sol, i) =>
        i === index ? { ...sol, [field]: value } : sol
      );
      return updatedSolutions;
    });
  };

  const addSolution = () => {
    setSolutions([
      ...solutions,
      { solution: null, duration: "", frequency: "", remarks: "" },
    ]);
  };

  const removeSolution = (index) => {
    setSolutions((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    let errors = {};
    if (!formData.prescribed_by) errors.prescribed_by = "Required";
    if (solutions.length === 0)
      errors.solutions = "At least one solution is required";
    solutions.forEach((sol, i) => {
      if (!sol.solution) errors[`solution_${i}`] = "Required";
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(triggerLoader());
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      dispatch(triggerLoader());
      return;
    }
    try {
      const res = await configServ.createPrescription({
        ...formData,
        response_id,
        created_by: user_id,
        solutions,
        customer_id: customer_id,
        // enquiry_id :enquiry.id,
        diagnosis_id: diagnosis_id,
      });
      if (res.status === 200) {
        handleReset();
        onSuccess && onSuccess(); // <-- call parent reset
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Prescription created successfully.",
        });
      } else {
        toast.error("Prescription creation failed.");
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleReset = () => {
    setSolutions([]);
    setFormData({ prescribed_by: "", remarks: "" });
    setValidationErrors({});
  };

  return (
    <>
      <Toaster />
      <Box sx={{ backgroundColor: "#f7f7f7", borderRadius: 2 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="#1e61b4"
            gutterBottom
          >
            Prescription
          </Typography>
          <form onSubmit={handleSubmit} onReset={handleReset}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={practitioners.map((option) => option.fname || "")}
                  value={formData.prescribed_by || ""}
                  onChange={(e, newValue) =>
                    setFormData({ ...formData, prescribed_by: newValue })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Prescribed By"
                      error={!!validationErrors.prescribed_by}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Typography
              variant="subtitle1"
              fontWeight="bold"
              gutterBottom
              sx={{ mt: 2 }}
            >
              Solutions
            </Typography>
            {solutions.map((sol, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <Autocomplete
                    options={itemList}
                    getOptionLabel={(option) => option.label}
                    value={sol.solution || null}
                    onChange={(e, value) =>
                      handleSolutionChange(index, "solution", value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Solution"
                        error={!!validationErrors[`solution_${index}`]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={2.5}>
                  <TextField
                    fullWidth
                    label="Duration"
                    onChange={(e) =>
                      handleSolutionChange(index, "duration", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={5} md={2.5}>
                  <TextField
                    fullWidth
                    label="Frequency"
                    onChange={(e) =>
                      handleSolutionChange(index, "frequency", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={5} md={2.5}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    onChange={(e) =>
                      handleSolutionChange(index, "remarks", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton onClick={() => removeSolution(index)}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button startIcon={<AddCircle />} onClick={addSolution}>
              Add Solution
            </Button>
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Button type="submit" variant="contained" startIcon={<Save />}>
                Prescribe
              </Button>
              <Button
                type="reset"
                variant="outlined"
                startIcon={<RotateLeft />}
              >
                Reset
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default memo(PrescriptionDetailsCustomer);
